import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { EyeCloseIcon, EyeOpenIcon } from "@/assets/icons";
import ColorBasedSelect from "@/components/dataset/datasetList/ColorBasedSelect";
import ColorScaleSelect from "@/components/dataset/datasetList/ColorScaleSelect";
import { DatasetStyleSelection } from "@/components/dataset/datasetList/DatasetStyleSelection/DatasetStyleSelection";
import { DatasetTypeIcon } from "@/components/dataset/datasetList/DatasetTypeIcon";
import OpacitySlider from "@/components/dataset/datasetList/OpacitySlider";
import SpatialHeader from "@/components/spatialAnalysis/header";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { MAX_RANKING_INPUT } from "@/constants/dataset";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useDataset } from "@/hooks/map/useDataset";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { useStepValue } from "@/hooks/useStepValue";
import { RootState } from "@/store";
import {
  setShowComparisonPopup,
  toggleDatasetExplorerPopup
} from "@/store/slice/uiSlice";
import { ColorScaleMethod, EColorLabel, EColorNames } from "@/types/dataset";
import {
  getAvailableDatasets,
  getColorBasedOnDefault,
  getFillStepInputConfig
} from "@/utils/dataset";
import { isRankableAttribute } from "@/utils/map/rank";
import { buildCustomLegendCallbacks } from "@/utils/mapLegend";

import { twMerge } from "tailwind-merge";

export default function MapPreviewMenu() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { datasetData, datasetMetadataSchema, poiParameterState } =
    location?.state || {};

  const {
    isNewUIDatasetConfigEnabled,
    isRankingLabelEnabled,
    isCustomMapVisualizationLegendEnabled
  } = useAppFeatures();

  const [searchParams] = useSearchParams();
  const { datasets, selectedDatasetExplorer, rawDatasets } = useSelector(
    (state: RootState) => state.dataset
  );
  const availableDatasets = getAvailableDatasets(datasets);
  const { map } = useMap();

  const datasetActions = useDataset();
  const {
    handleToggleDatasetVisibility,
    handleChangeSelectedCategoryDataset,
    handleChangeColorDataset,
    handleAddSpatialAnalysisDataset,
    handleDisplayName,
    handleDisplayNameVisibility,
    handleDisplayNameColor,
    handleRankConfigChange,
    handleChangeStrokeWidthDataset,
    handleChangeStrokeAttributeDataset,
    handleChangeColorScaleMethod,
    handleChangeCustomCategoryStepCount,
    handleChangeCustomRangeStepCount,
    getAttributeNumericRange,
    getAttributeHasEmptyValues
  } = datasetActions;
  const { handleResetDatasetToHistoryDataset } = useMap();

  const datasetId = searchParams.get("id") || "";
  const isExplorerMode = searchParams.get("explorer") === "true";
  const isPoiMode = searchParams.get("poi") === "true";

  const data = availableDatasets[datasetId] || {};

  const handleBackToTableView = () => {
    handleResetDatasetToHistoryDataset();
    dispatch(
      setShowComparisonPopup({
        isShow: false,
        isFromAnalysisResult: false,
        isFromAnalysisSummary: false,
        isLimitShow: false
      })
    );
    if (isExplorerMode) {
      navigate("/", {
        state: { datasetExplorerState: selectedDatasetExplorer }
      });
      dispatch(toggleDatasetExplorerPopup(true));
    } else if (isPoiMode) {
      navigate("/", {
        state: { poiParameterState }
      });
      dispatch(toggleDatasetExplorerPopup(true));
    } else {
      navigate(`/dataset-management/${datasetId}`);
    }
  };

  const totalRows = rawDatasets[data?.data?.id]?.rows.length || 0;
  const { stepValue, maxStepValue, setStepValue, resetStepValue } =
    useStepValue({
      totalRows
    });

  useEffect(() => {
    if (!datasetData || !datasetMetadataSchema || !map) return;
    handleAddSpatialAnalysisDataset(datasetData, datasetMetadataSchema, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetData, datasetMetadataSchema, map]);

  if (!data || !Object.keys(data)?.length) return null;

  // Pre-calculate strokeAttribute to avoid recalculating it in callbacks
  const strokeAttribute = data.categories.find(
    (cat) => cat.attribute_name === data.style.stroke?.attribute_name
  );

  const handleStepValueChange = (step: number) => {
    if (step >= 2 && step <= 10) {
      handleChangeSelectedCategoryDataset(
        data,
        data.selectedCategory.attribute_name,
        data.style.colorFamily,
        step
      );
    }
  };

  const { fillStepConfig, customRangeBounds, customRangeHasEmptyValues } =
    getFillStepInputConfig({
      data,
      isCustomMapVisualizationLegendEnabled,
      stepValue,
      setStepValue,
      getAttributeNumericRange,
      getAttributeHasEmptyValues,
      onChangeCustomCategoryStepCount: handleChangeCustomCategoryStepCount,
      onChangeCustomRangeStepCount: handleChangeCustomRangeStepCount,
      onDefaultStepChange: handleStepValueChange
    });

  const handleStrokeStepValueChange = (step: number) => {
    // Keep the controlled step input (reads `stepValue`) in sync, then recolor.
    setStepValue(step);
    if (step >= 2 && step <= 10) {
      handleChangeStrokeAttributeDataset(
        data,
        data.style.stroke?.attribute_name || "",
        data.style.colorFamily,
        step
      );
    }
  };

  // Stroke step input: same custom-method routing as fill, but a value-based
  // step change recolors the stroke (lines), not the fill.
  const { fillStepConfig: strokeStepConfig } = getFillStepInputConfig({
    data,
    isCustomMapVisualizationLegendEnabled,
    stepValue,
    setStepValue,
    getAttributeNumericRange,
    getAttributeHasEmptyValues,
    onChangeCustomCategoryStepCount: handleChangeCustomCategoryStepCount,
    onChangeCustomRangeStepCount: handleChangeCustomRangeStepCount,
    onDefaultStepChange: handleStrokeStepValueChange
  });

  const handleCheckDisplayLabel = (e: boolean) => {
    handleDisplayNameVisibility(data, e);
  };

  const handleSelectCategoryLabel = (selectedCategory: string) => {
    const category = data.categories.find(
      (category) => category.attribute_name === selectedCategory
    );

    if (!category) return;

    // Reconcile rank state to the newly selected column: ordinal attributes
    // aren't rankable, so enabling rank must follow the column's data type.
    const schema =
      data.data.schema.length > 0 ? data.data.schema : data.categories;
    const isEnabled = isRankableAttribute(schema, category.attribute_name);

    // Pass `category` so rank is merged against the newly selected column,
    // not the stale one — otherwise the rank label never renders.
    handleRankConfigChange(data.data.id, data.style, { isEnabled, category });
    handleDisplayName(data, category);
  };

  const handleChangeLabelColor = (color: string) => {
    handleDisplayNameColor(data, color as EColorLabel);
  };

  const maxRankingInput = Math.min(
    rawDatasets[data.data.id]?.rows.length || 0,
    MAX_RANKING_INPUT
  );

  const handleChangeMaxRankingInput = (newValue: number) => {
    if (newValue > maxRankingInput) newValue = maxRankingInput;
    if (newValue < 1) newValue = 1;

    handleRankConfigChange(data.data.id, data.style, {
      maxRanking: newValue
    });
  };

  const handleChangeMaxRankingSlider = (arrayValue: number[]) => {
    let value = arrayValue[0];
    if (value < 1) value = 1;

    handleRankConfigChange(data.data.id, data.style, {
      maxRanking: value
    });
  };

  // Custom-legend callbacks are shared by the Fill panel and the (line) Stroke
  // panel — both drive the same config slots, so build them once. The Simple
  // panel's Reset isn't wired here (matches the previous Preview behaviour).
  const {
    singleStyle: singleStyleCallbacks,
    customCategory: customCategoryCallbacks,
    customRange: customRangeCallbacks
  } = buildCustomLegendCallbacks(data.data.id, datasetActions, {
    resetStepValue,
    customRangeBounds,
    customRangeHasEmptyValues,
    includeSingleStyleReset: false
  });

  return (
    <div
      className={twMerge(
        "absolute top-[30px] left-[16px] z-[9] border-gainsboro bg-white rounded-lg w-[338px] max-h-[90vh] overflow-hidden",
        "flex flex-col",
        Object.keys(availableDatasets).length > 0 ? "" : "min-h-[224px]"
      )}
    >
      <SpatialHeader title="Preview in Map" onClose={handleBackToTableView} />
      {/* Scrolls within the panel's max height once the content overflows
          (min-h-0 lets this flex child shrink below its content size). */}
      <div className="p-4 flex flex-col gap-3 flex-1 min-h-0 overflow-y-auto">
        <div>
          <p className="text-grey text-xs mb-1">Selected Dataset</p>
          <div className="h-full flex flex-row items-center gap-2 border border-gainsboro rounded-xl py-2 px-3">
            <div className="flex flex-row items-center gap-2 overflow-hidden">
              {<DatasetTypeIcon type={data.properties.spatialType} />}
              <p className="text-[12px] text-txtHeading font-regular truncate">
                {data.data.name}
              </p>
            </div>

            <button
              data-testid="visibility-button"
              aria-label={
                data.properties.isVisible
                  ? `Hide dataset ${data.data.name}`
                  : `Show dataset ${data.data.name}`
              }
              className="flex bg-white items-center justify-center px-1 ml-auto"
              onClick={() => handleToggleDatasetVisibility(data.data.id)}
            >
              {data.properties.isVisible ? (
                <EyeOpenIcon className="fill-dim-gray" />
              ) : (
                <EyeCloseIcon className="fill-dim-gray" />
              )}
            </button>
          </div>
        </div>
        {isNewUIDatasetConfigEnabled ? (
          <Accordion type="single" collapsible className="flex flex-col gap-2">
            <DatasetStyleSelection
              type="fill"
              data={data}
              isEditMode={true}
              isLayerVisible={data.properties.isVisible}
              isSpatialAnalysis={false}
              isNewUIDatasetConfigEnabled={isNewUIDatasetConfigEnabled}
              isCustomMapVisualizationLegendEnabled={
                isCustomMapVisualizationLegendEnabled
              }
              categoryConfig={{
                value: data.selectedCategory?.attribute_name,
                onChange: (value) =>
                  handleChangeSelectedCategoryDataset(
                    data,
                    value,
                    data.style.colorFamily
                  )
              }}
              colorBasedOnConfig={{
                value: getColorBasedOnDefault(
                  data.style.colorScaleMethod,
                  data.selectedCategory?.data_type
                ),
                dataType: data.selectedCategory?.data_type,
                onChange: (value) =>
                  handleChangeColorScaleMethod(
                    data.data.id,
                    value as ColorScaleMethod
                  )
              }}
              colorConfig={{
                value: data.style.colorFamily,
                onChange: (selectedColorFamily) => {
                  handleChangeColorDataset(
                    data.data.id,
                    selectedColorFamily as EColorNames,
                    data.style.colorRanges,
                    data.selectedCategory.attribute_name,
                    data.properties.spatialType,
                    data.selectedCategory.data_type,
                    stepValue ?? data.style.colorSteps
                  );
                },
                ...fillStepConfig
              }}
              singleStyle={singleStyleCallbacks}
              customCategory={customCategoryCallbacks}
              customRange={customRangeCallbacks}
              totalRows={totalRows}
            />

            <DatasetStyleSelection
              type="label"
              data={data}
              totalRows={totalRows}
              isEditMode={true}
              isLayerVisible={data.properties.isVisible}
              isSpatialAnalysis={false}
              isRankingLabelEnabled={isRankingLabelEnabled}
              categoryConfig={{
                value: data.style.labelConfig?.attribute_name || "",
                onChange: handleSelectCategoryLabel,
                title: "Column Option"
              }}
              colorConfig={{
                value: data.style.labelConfig?.color_scale,
                onChange: handleChangeLabelColor
              }}
              labelConfig={{
                show: data.style.labelConfig.show_label,
                onCheck: handleCheckDisplayLabel
              }}
              rankConfig={{
                onCheck: (value) => {
                  handleRankConfigChange(data.data.id, data.style, {
                    isEnabled: value
                  });
                },
                onChangeInput: handleChangeMaxRankingInput,
                onChangeSlider: handleChangeMaxRankingSlider
              }}
            />

            <DatasetStyleSelection
              type="stroke"
              isNewUIDatasetConfigEnabled={isNewUIDatasetConfigEnabled}
              data={data}
              totalRows={totalRows}
              isLayerVisible={data.properties.isVisible}
              isEditMode={true}
              isSpatialAnalysis={false}
              isRankingLabelEnabled={isRankingLabelEnabled}
              isCustomMapVisualizationLegendEnabled={
                isCustomMapVisualizationLegendEnabled
              }
              categoryConfig={{
                value: data.style.stroke?.attribute_name || "",
                onChange: (value) => {
                  handleChangeStrokeAttributeDataset(
                    data,
                    value,
                    data.style.colorFamily,
                    data.style.colorSteps || 10
                  );
                },
                title: "Column Option"
              }}
              // Lines style the stroke via the custom legend (method + config
              // shared with fill); applicable methods come from the stroke type.
              colorBasedOnConfig={{
                value: getColorBasedOnDefault(
                  data.style.colorScaleMethod,
                  strokeAttribute?.data_type || data.selectedCategory.data_type
                ),
                dataType:
                  strokeAttribute?.data_type || data.selectedCategory.data_type,
                onChange: (value) =>
                  handleChangeColorScaleMethod(
                    data.data.id,
                    value as ColorScaleMethod
                  )
              }}
              colorConfig={{
                value: data.style.colorFamily,
                onChange: (selectedColorFamily) => {
                  handleChangeColorDataset(
                    data.data.id,
                    selectedColorFamily as EColorNames,
                    data.style.colorRanges,
                    data.style.stroke?.attribute_name ||
                      data.selectedCategory.attribute_name,
                    data.properties.spatialType,
                    strokeAttribute?.data_type ||
                      data.selectedCategory.data_type
                  );
                },
                ...strokeStepConfig
              }}
              singleStyle={singleStyleCallbacks}
              customCategory={customCategoryCallbacks}
              customRange={customRangeCallbacks}
              strokeConfig={{
                value: data.style.stroke.width,
                onChange: (width: number) => {
                  handleChangeStrokeWidthDataset(data.data.id, width);
                }
              }}
            />
          </Accordion>
        ) : (
          <div className=" border-gainsboro pt-2 flex flex-col gap-2">
            <ColorBasedSelect
              value={data.selectedCategory.attribute_name}
              onValueChange={(value) =>
                handleChangeSelectedCategoryDataset(
                  data,
                  value,
                  data.style.colorFamily
                )
              }
              categories={data.categories}
              disabled={!data.properties.isVisible}
            />
            <ColorScaleSelect
              value={data.style.colorFamily}
              onValueChange={(colorFamily) => {
                handleChangeColorDataset(
                  data.data.id,
                  colorFamily as EColorNames,
                  data.style.colorRanges,
                  data.selectedCategory.attribute_name,
                  data.properties.spatialType,
                  data.selectedCategory.data_type
                );
              }}
              stepValue={stepValue}
              maxStepValue={maxStepValue}
              onStepValueChange={setStepValue}
            />
            <OpacitySlider data={data} isSpatialAnalysis={false} />
          </div>
        )}
      </div>
      {/* Fixed footer — stays put while the content above scrolls. */}
      <div className="w-full px-4 pb-4 pt-3">
        <Button className="w-full" onClick={handleBackToTableView}>
          Back to Table View
        </Button>
      </div>
    </div>
  );
}
